<template>
    <div class="modal-content-animated"
         :class="{ 'is-animated': showBits }">
        <h1 v-if="showBits"
            class="bits">
            <img :src="asset_cdn('assets/missions/coin.svg')"
                 :alt="`${getTrans('quests-dashboard.reward', 'Reward')}: ${bits} ${currency}s`"
                 :title="`${getTrans('quests-dashboard.reward', 'Reward')}: ${bits} ${currency}s`"
                 width="50">
            <span class="bits__number">
                {{ bits }}
            </span>
            <span class="bits__currency">
                {{ currency }}S
            </span>
        </h1>
    </div>
</template>

<script setup lang="ts">
    import {
        onBeforeUnmount, onMounted, ref,
    } from 'vue';
    import { asset_cdn } from '@/js/helpers/helpers';
    import { getTrans } from '@/js/helpers/getTrans';
    import { REWARD_ANIMATION_DURATION_MS } from '@/js/quests/global/constants/rewardAnimation';

    interface Props {
        amount: number;
        currency?: string;
    }
    const props = withDefaults(defineProps<Props>(), {
        currency: '',
    });

    const bits = ref(0);
    const showBits = ref(false);

    const animationFrameId = ref<number | null>(null);
    const startTime = ref(performance.now());
    const ANIMATION_START_DELAY_MS = 1500;

    const animateCounter = (currentTime: number) => {
        const elapsedTime = currentTime - startTime.value - ANIMATION_START_DELAY_MS;
        const progress = Math.min(elapsedTime / REWARD_ANIMATION_DURATION_MS, 1);

        bits.value = Math.floor(progress * props.amount);

        if (progress < 1) {
            animationFrameId.value = requestAnimationFrame(animateCounter);
        }
    };

    const delayAnimation = (timestamp: number) => {
        const elapsed = timestamp - startTime.value;

        if (elapsed < ANIMATION_START_DELAY_MS) {
            animationFrameId.value = requestAnimationFrame(delayAnimation);

            return;
        }

        if (!showBits.value) {
            showBits.value = true;

            if (props.amount > 0) {
                animationFrameId.value = requestAnimationFrame(animateCounter);
            }
        }
    };

    onMounted(() => {
        animationFrameId.value = requestAnimationFrame(delayAnimation);
    });

    onBeforeUnmount(() => {
        if (animationFrameId.value !== null) {
            cancelAnimationFrame(animationFrameId.value);
        }
    });
</script>

<style scoped lang="scss">
@use "@/sass/design-system/variables" as variables;
@use "@/sass/partials/breakpoints" as breakpoints;

.modal-content-animated {
    overflow: hidden;
    max-height: 0;
    transition: max-height 1.4s ease-in-out;

    &.is-animated {
        max-height: 80px;
    }
}

.bits {
    display: flex;
    align-items: baseline;
    justify-content: center;
    margin-bottom: 16px;
    color: variables.$warning400;
    font-weight: 700;
    gap: 8px;
    text-align: center;
    text-transform: capitalize;
    transition: 1s filter linear;

    img {
        align-self: center;
    }

    &__number {
        min-width: 100px;
        font-size: 64px;
        line-height: 1;
    }

    &__currency {
        font-size: 24px;
    }

    @media (max-width: breakpoints.$md-breakpoint-max-width) {
        img {
            width: 60px;
        }
    }
}
</style>
